export const DEFAULT_CHAT_SETTINGS = Object.freeze({
    model: 'gpt-5.2',
    reasoning: 'medium',
    toolMode: 'balanced',
})

const ALLOWED_CHAT_MODELS = new Set(['gpt-5.2', 'gpt-5.4'])
const ALLOWED_REASONING_LEVELS = new Set(['low', 'medium', 'high'])
const ALLOWED_TOOL_MODES = new Set([
    'balanced',
    'ticket_research',
    'hudu_focus',
    'minimal',
])

const toSafeInteger = (value) => {
    const numericValue = Number(value)
    return Number.isFinite(numericValue) ? Math.max(0, Math.round(numericValue)) : null
}

export const isWelcomeMessage = (message) => {
    return String(message?.id || '').startsWith('welcome-')
}

export const normalizeChatSettings = (settings = null) => {
    const candidate = settings && typeof settings === 'object' ? settings : {}
    const model = String(candidate.model || DEFAULT_CHAT_SETTINGS.model)
        .trim()
        .toLowerCase()
    const reasoning = String(
        candidate.reasoning || DEFAULT_CHAT_SETTINGS.reasoning
    )
        .trim()
        .toLowerCase()
    const toolMode = String(candidate.toolMode || DEFAULT_CHAT_SETTINGS.toolMode)
        .trim()
        .toLowerCase()

    return {
        model: ALLOWED_CHAT_MODELS.has(model)
            ? model
            : DEFAULT_CHAT_SETTINGS.model,
        reasoning: ALLOWED_REASONING_LEVELS.has(reasoning)
            ? reasoning
            : DEFAULT_CHAT_SETTINGS.reasoning,
        toolMode: ALLOWED_TOOL_MODES.has(toolMode)
            ? toolMode
            : DEFAULT_CHAT_SETTINGS.toolMode,
    }
}

export const normalizeChatPreview = (value, maxLength = 140) => {
    const normalized = String(value || '')
        .replace(/\s+/g, ' ')
        .trim()

    if (!normalized) {
        return ''
    }

    return normalized.length > maxLength
        ? `${normalized.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`
        : normalized
}

export const stripMarkdownPreview = (value) => {
    return String(value || '')
        .replace(/```[\s\S]*?```/g, (block) =>
            block
                .replace(/```[\t ]*[a-z0-9_-]*\n?/gi, '')
                .replace(/```/g, '')
        )
        .replace(/`([^`]+)`/g, '$1')
        .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '$1')
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1')
        .replace(/^#{1,6}\s+/gm, '')
        .replace(/^>+\s?/gm, '')
        .replace(/^[-*+]\s+/gm, '')
        .replace(/^\d+\.\s+/gm, '')
        .replace(/\*\*([^*]+)\*\*/g, '$1')
        .replace(/__([^_]+)__/g, '$1')
        .replace(/(^|[^*])\*([^*]+)\*(?!\*)/g, '$1$2')
        .replace(/(^|[^_])_([^_]+)_(?!_)/g, '$1$2')
        .replace(/~~([^~]+)~~/g, '$1')
        .replace(/<[^>]+>/g, ' ')
}

export const normalizeChatAttachment = (attachment) => {
    if (!attachment || typeof attachment !== 'object') {
        return null
    }

    const id = String(attachment.id || '').trim()
    if (!id) {
        return null
    }

    return {
        id,
        type: 'image',
        fileName: String(attachment.fileName || 'Image').trim() || 'Image',
        mimeType: String(attachment.mimeType || 'image/png').trim().toLowerCase(),
        sizeBytes: toSafeInteger(attachment.sizeBytes),
        width: toSafeInteger(attachment.width),
        height: toSafeInteger(attachment.height),
        previewUrl: String(attachment.previewUrl || '').trim(),
        status: String(attachment.status || 'ready').trim().toLowerCase() || 'ready',
    }
}

export const normalizeChatAttachments = (attachments = []) => {
    return Array.isArray(attachments)
        ? attachments.map(normalizeChatAttachment).filter(Boolean)
        : []
}

export const getChatAttachmentSummary = (attachments = []) => {
    const normalizedAttachments = normalizeChatAttachments(attachments)
    if (normalizedAttachments.length === 0) {
        return ''
    }

    if (normalizedAttachments.length === 1) {
        return `Image: ${normalizedAttachments[0].fileName}`
    }

    return `${normalizedAttachments.length} images attached`
}

export const getVisibleChatMessages = (messages = []) => {
    return Array.isArray(messages)
        ? messages.filter((message) => !isWelcomeMessage(message))
        : []
}

export const getChatMessageCount = (chat) => {
    const explicitCount = Number(chat?.messageCount)
    if (Number.isFinite(explicitCount) && explicitCount >= 0) {
        return explicitCount
    }

    return getVisibleChatMessages(chat?.messages).length
}

export const hasVisibleChatMessages = (chat) => {
    if (typeof chat?.hasMessages === 'boolean') {
        return chat.hasMessages || getVisibleChatMessages(chat?.messages).length > 0
    }

    return getChatMessageCount(chat) > 0
}

export const isTicketBoundChat = (chat) => {
    return String(chat?.context?.activeContext?.type || '').toLowerCase() === 'ticket'
}

export const isRecentVisibleChat = (chat) => {
    return hasVisibleChatMessages(chat)
}

export const getChatPreviewText = (chat) => {
    if (chat?.lastMessagePreview) {
        return normalizeChatPreview(stripMarkdownPreview(chat.lastMessagePreview))
    }

    const visibleMessages = getVisibleChatMessages(chat?.messages)
    const lastMessage = visibleMessages[visibleMessages.length - 1]
    const messagePreview = normalizeChatPreview(stripMarkdownPreview(lastMessage?.text || ''))
    if (messagePreview) {
        return messagePreview
    }

    return normalizeChatPreview(getChatAttachmentSummary(lastMessage?.attachments))
}
